import type { ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useSession } from '../lib/session.js';

export const SITE_NAME = 'AstrologerMatch';

// Ported from prototype/src/App.jsx (Logo, Header, footer). The prototype's "Prototype · sample
// data" bar and public "Admin" links are prototype-only conveniences and are not shipped.

// A tighter header-specific lockup (head+shoulders+robot beside the wordmark, scaled so the
// wordmark reads clearly at header height) — the full rectangle logo (header-logo(-light), still
// used for the footer/emails/OG image) reads as square-ish and near-illegible at 44-56px tall.
// `dark` picks the cream-wordmark variant for the on-dark header (home, profile pages) — the
// brown default doesn't have enough contrast there.
function Logo({ dark }: { dark: boolean }) {
  const base = dark ? 'header-lockup-light' : 'header-lockup';
  return (
    <Link to="/" className="logo">
      <picture>
        <source
          type="image/webp"
          srcSet={`/brand/${base}-200.webp 1x, /brand/${base}-400.webp 2x, /brand/${base}-600.webp 3x`}
        />
        <img
          src={`/brand/${base}-400.png`}
          srcSet={`/brand/${base}-200.png 1x, /brand/${base}-400.png 2x, /brand/${base}-600.png 3x`}
          width={200}
          height={57}
          alt="AstrologerMatch"
        />
      </picture>
    </Link>
  );
}

function Header() {
  const { pathname } = useLocation();
  const { session } = useSession();
  const dark = pathname === '/' || pathname.startsWith('/astrologers/');
  const loggedIn = session?.role === 'astrologer';
  const partnerLoggedIn = session?.role === 'partner';
  return (
    <header className={`site-header${dark ? ' on-dark' : ''}`}>
      <div className="wrap header-inner">
        <Logo dark={dark} />
        <nav className="main-nav" aria-label="Main">
          <NavLink to="/" end>
            Find an astrologer
          </NavLink>
          <NavLink to={loggedIn ? '/dashboard' : '/astrologer'}>
            {loggedIn ? 'My dashboard' : 'For astrologers'}
          </NavLink>
          <NavLink to="/partner">
            {partnerLoggedIn ? 'My partner dashboard' : 'Partner with us'}
          </NavLink>
        </nav>
      </div>
    </header>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="site-footer">
      <div className="wrap footer-inner">
        <span>
          © {year} {SITE_NAME} · A Cyntra360Hub product
        </span>
        <span className="footer-links">
          <Link to="/">Find an astrologer</Link>
          <Link to="/astrologer">List your practice</Link>
          <Link to="/partner">Partner with us</Link>
        </span>
      </div>
      <nav className="wrap footer-legal" aria-label="Legal">
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/advertiser-policy">Advertiser Policy</Link>
        <Link to="/refund-policy">Refund &amp; Cancellation</Link>
        <Link to="/contact">Contact</Link>
        <Link to="/grievance">Grievance Officer</Link>
      </nav>
      <div className="wrap disclaimer">
        Paid placements are labelled “Ad”. {SITE_NAME} does not provide astrology services; you
        contact and pay astrologers directly.
      </div>
    </footer>
  );
}

export function Layout({ children }: { children: ReactNode }) {
  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <Header />
      <main id="main">{children}</main>
      <Footer />
    </>
  );
}
